/**
 * Ask Chase - the client half.
 *
 * Everything that costs money or holds a key happens in the `ask-chase` edge
 * function. This file only carries the card up and the answer back, and reads
 * the thread so a returning user sees what they said last time.
 */

import { supabase } from '../lib/supabase';

/**
 * A conversation belongs to the CARD, not to the row the user opened.
 *
 * The same Charizard arrives as a `cards` uuid from a binder and as
 * `tcg_base1_4` from the set browser. Keying on either id would split one
 * conversation into two that never meet, so the key is what the card actually
 * is. The number is trimmed at the slash for the same reason the price matcher
 * does it: we store "004/102" and look up "4".
 */
export function cardKeyFor(card) {
  if (!card?.name) return null;
  return [
    card.name,
    card.set_name || '',
    String(card.set_number || '').split('/')[0],
  ].map((p) => String(p || '').trim().toLowerCase()).join('|');
}

/**
 * The messages already in this card's thread, oldest first.
 *
 * One query, not two. It used to look the thread up and then fetch its messages,
 * which is two round trips to show a conversation that is usually four lines
 * long, and the delay was visible every time the chat opened.
 *
 * Empty array for a card never discussed, which is also what a signed-out or
 * failed read returns: an empty thread is a fine thing to open.
 */
export async function loadThread(card) {
  const key = cardKeyFor(card);
  if (!key) return [];

  const { data, error } = await supabase
    .from('chase_threads')
    .select('id, chase_messages(id, role, content, sources, created_at)')
    .eq('card_key', key)
    .maybeSingle();

  if (error || !data) {
    if (error) console.warn('[Chase] thread read failed:', error.message);
    return [];
  }

  return (data.chase_messages || [])
    .sort((a, b) => String(a.created_at).localeCompare(String(b.created_at)));
}

/**
 * Does this card already have a conversation?
 *
 * Asked once when the card opens, so tapping Chase can go straight into an
 * existing thread instead of offering the same three questions to somebody who
 * is already mid-conversation with him.
 *
 * Counts rather than reads. The answer is a boolean and the messages may not be
 * wanted at all.
 */
export async function threadExists(card) {
  const key = cardKeyFor(card);
  if (!key) return false;

  const { count, error } = await supabase
    .from('chase_threads')
    .select('id', { count: 'exact', head: true })
    .eq('card_key', key);

  return !error && (count || 0) > 0;
}

/**
 * Ask him something.
 *
 * @param {Object}  card          the card row on screen
 * @param {string}  door          'pricing' | 'facts' | 'custom'
 * @param {string}  message       the typed question, for the custom door
 * @param {string} [selectedTier] the ladder row they are looking at, e.g. "PSA 9"
 * @returns {Promise<{answer: string}>}
 * @throws  when he could not answer, with a message worth showing
 */
export async function askChase({ card, door, message = '', selectedTier = null }) {
  const { data, error } = await supabase.functions.invoke('ask-chase', {
    body: {
      cardName: card?.name,
      setName: card?.set_name || '',
      setNumber: card?.set_number || '',
      variant: card?.variant || '',
      grade: card?.grade || '',
      // The catalogue id, which is what lets the function look the card up in
      // TCGdex rather than trusting the model to remember who drew it.
      tcgdexId: card?.card_id || null,
      selectedTier,
      door,
      message,
    },
  });

  // invoke() reports transport failures in `error` and the function's own 4xx
  // and 5xx bodies in `data`, so both have to be checked or a refusal reads as
  // a success with no answer in it.
  if (error) throw new Error(error.message || 'Chase could not answer just now.');
  if (data?.error) throw new Error(data.error);
  if (!data?.answer) throw new Error('Chase could not answer just now.');

  return data;
}

const chaseService = { askChase, loadThread, threadExists, cardKeyFor };
export default chaseService;
